import React, { ComponentProps, MutableRefObject, useMemo, useState } from 'react'

import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { ColumnState } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import Select from 'react-select'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { useAppDispatch, useAppSelector } from '@/client/hooks'
import {
  setColumnState,
  setCurrentPresetId,
  setSearch,
  setSelectedStaff,
  setStage,
  setViewAll,
} from '@/client/workQueuesSlice'
import { MODALS, setActiveModal } from '@/features/FileManager/fileManagerSlice'
import Modal from '@/features/Modals/Modal'
import { KeyCheckbox } from '@/key-components/KeyCheckbox'
import { KeyInput } from '@/key-components/KeyInput'
import { KeyLabel } from '@/key-components/KeyLabel'
import { KeyColors } from '@/key-components/utils/KeyColors'
import {
  useGetLoggedUserQuery,
  useGetStaffUsersQuery,
  useSetUserDefaultPresetMutation,
} from '@/services/user'
import {
  WorkQueuesPreset,
  useCreateWorkQueuesPresetMutation,
  useDeleteWorkQueuesPresetMutation,
  useListWorkQueuesPresetsQuery,
  useUpdateWorkQueuesPresetMutation,
} from '@/services/workQueuesPreset'
import { Application } from '@/types/unlock'
import { toastDefaultOptions } from '@/utils/toast'

type OptionType = {
  companyPreset: boolean
  value: string
  label: string
}
type OptionsType = { label: string; options: Array<OptionType> }

type PresetsMenuProps = ComponentProps<'button'> & {
  gridRef: MutableRefObject<AgGridReact<Application>>
  handleSetAdminFilters: (filter_model: object) => void
  handleSetAplicationStates: (filter_model: object) => void
}

export const PresetsMenu = ({
  gridRef,
  handleSetAdminFilters,
  handleSetAplicationStates,
}: PresetsMenuProps) => {
  const dispatch = useAppDispatch()
  const toggle = () => dispatch(setActiveModal(null))

  const activeModal = useAppSelector((state) => state.fileManager.activeModal)
  const stage = useAppSelector((state) => state.workQueues.stage)
  const currentPresetId = useAppSelector((state) => state.workQueues.currentPresetId)
  const search = useAppSelector((state) => state.workQueues.search)
  const selectedStaff = useAppSelector((state) => state.workQueues.selectedStaff)
  const viewAll = useAppSelector((state) => state.workQueues.viewAll)

  const [createPreset] = useCreateWorkQueuesPresetMutation()
  const [updatePreset] = useUpdateWorkQueuesPresetMutation()
  const [deletePreset] = useDeleteWorkQueuesPresetMutation()
  const [setUserDefaultPreset] = useSetUserDefaultPresetMutation()

  const { data: presets } = useListWorkQueuesPresetsQuery()
  const { data: user } = useGetLoggedUserQuery()
  const { data: staffUsers } = useGetStaffUsersQuery()

  const [showDropdown, setShowDropdown] = useState(false)
  const [presetName, setPresetName] = useState('')
  const [selectedPreset, setSelectedPreset] = useState(null)
  const [companyView, setCompanyView] = useState(false)

  const sendFieldsFilters = (filter_model) => {
    const _parseFilterValues = (values) => {
      if (!values) return []

      if (values?.includes(',')) {
        return values?.split(',')
      } else {
        return [values]
      }
    }

    handleSetAdminFilters(_parseFilterValues(filter_model?.deal_teams?.filter))
    handleSetAplicationStates(_parseFilterValues(filter_model?.application_state?.filter))
  }

  const currentPreset: WorkQueuesPreset = useMemo(() => {
    const preset = presets?.find((it) => it.uuid === currentPresetId)
    setPresetName(preset?.name)
    return preset
  }, [presets, currentPresetId])

  const allowDeleteOrUpdatePreset = useMemo(() => {
    if (user?.is_superuser) return true
    if (currentPreset?.user == user?.id) return true
  }, [currentPreset])

  const formatPresetDefaultName = (preset) => {
    const defaultPresetId = user?.data?.default_work_queues_preset
    return preset.id === defaultPresetId ? `${preset.name} (Default)` : preset.name
  }
  const overlay = 'absolute top-0 left-0 h-screen w-screen z-10'
  const after =
    'after:absolute after:border-t after:border-l after:bg-white after:-top-3 after:right-4 after:w-6 after:h-6 after:rotate-45 after:-z-10'

  const onSelectPreset = (presetUUID) => {
    if (!presetUUID) return
    if (!gridRef?.current?.columnApi) return

    const preset = presets?.find((it) => it.uuid === presetUUID)
    if (!preset) return

    if (!presetOptions) return

    const selected = presetOptions
      .map((groupOption) => groupOption.options.find((option) => option.value === preset.uuid))
      .find((preset) => preset)

    const { column_state, filter_model, subscribers, stage, search, view_all } = preset
    const ids = subscribers?.split(',')
    const selectedStaff = staffUsers?.filter((user) => ids?.includes(String(user.id)))

    gridRef.current.columnApi.applyColumnState({
      state: column_state as ColumnState[],
      applyOrder: true,
    })
    gridRef.current.api.setFilterModel(filter_model)
    dispatch(setCurrentPresetId(presetUUID))
    dispatch(setSearch(search))
    dispatch(setStage(stage))
    dispatch(setSelectedStaff(selectedStaff))
    dispatch(setViewAll(view_all))
    dispatch(setColumnState(column_state as ColumnState[]))
    sendFieldsFilters(filter_model)
    setSelectedPreset(selected)
  }

  const handleUpdatePreset = () => {
    const grid = gridRef.current
    if (!grid) return

    const payload: Partial<WorkQueuesPreset> = {
      uuid: currentPreset.uuid,
      user: user.id,
      search,
      stage,
      view_all: viewAll,
      subscribers: selectedStaff?.map((staff) => staff.id).join(','),
      column_state: grid.columnApi.getColumnState(),
      filter_model: grid.api.getFilterModel(),
    }
    updatePreset(payload)
      .unwrap()
      .then(() => toast.success('View updated successfully!', { ...toastDefaultOptions }))
      .catch(() => toast.error('Server error', { ...toastDefaultOptions }))
    setShowDropdown(false)
  }

  const handleSaveAs = (presetName: string) => {
    const grid = gridRef.current
    if (!grid) return

    const payload: Partial<WorkQueuesPreset> = {
      name: presetName,
      company_preset: companyView,
      user: user.id,
      search,
      stage,
      view_all: viewAll,
      subscribers: selectedStaff?.map((staff) => staff.id).join(','),
      column_state: grid.columnApi.getColumnState(),
      filter_model: grid.api.getFilterModel(),
    }
    createPreset(payload)
      .unwrap()
      .then(() => toast.success('View saved successfully!', { ...toastDefaultOptions }))
      .catch(() => toast.error('Server error', { ...toastDefaultOptions }))
    setShowDropdown(false)
    setCompanyView(false)
    dispatch(setActiveModal(null))
  }

  const handleSetDefaultPreset = (presetUUID) => {
    setUserDefaultPreset({
      default_work_queues_preset: presetUUID,
    })
      .unwrap()
      .then(() => toast.success('Default view defined successfully!', { ...toastDefaultOptions }))
      .catch(() => toast.error('Server error', { ...toastDefaultOptions }))
    setShowDropdown(false)
  }

  const handleDeletePreset = (presetUUID) => {
    deletePreset(presetUUID)
      .unwrap()
      .then(() => toast.success('View deleted successfully!', { ...toastDefaultOptions }))
      .catch(() => toast.error('Server error', { ...toastDefaultOptions }))
    setShowDropdown(false)
    setSelectedPreset(null)
  }

  const handleRenamePreset = (name: string) => {
    const grid = gridRef.current
    if (!grid) return

    const payload: Partial<WorkQueuesPreset> = {
      uuid: currentPreset.uuid,
      name,
    }
    updatePreset(payload)
      .unwrap()
      .then(() => toast.success('View renamed successfully!', { ...toastDefaultOptions }))
      .catch(() => toast.error('Server error', { ...toastDefaultOptions }))
    dispatch(setActiveModal(null))
    setShowDropdown(false)
  }

  const presetOptions: OptionsType[] = useMemo(() => {
    if (presets)
      return [
        {
          label: 'Company View',
          options: [...presets]
            .filter((preset) => preset.company_preset)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((it) => ({
              value: it.uuid,
              label: formatPresetDefaultName(it),
              companyPreset: it.company_preset,
            })),
        },
        {
          label: 'Personal View',
          options: [...presets]
            .filter((preset) => preset.user === user?.id && !preset.company_preset)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((it) => ({
              value: it.uuid,
              label: formatPresetDefaultName(it),
              companyPreset: it.company_preset,
            })),
        },
      ]
  }, [user, presets])

  useMemo(() => {
    if (user?.data?.default_work_queues_preset && presets?.length > 0) {
      const preset = presets.find((preset) => preset.id === user?.data?.default_work_queues_preset)
      if (preset?.uuid && !selectedPreset && presetOptions?.length) {
        onSelectPreset(preset.uuid)
      }
    }
  }, [user?.data?.default_work_queues_preset, presets, presetOptions, gridRef?.current?.columnApi])

  useMemo(() => {
    if (currentPreset?.uuid) {
      onSelectPreset(currentPreset?.uuid)
    }
  }, [presetOptions])

  return (
    <>
      <Select
        className='w-1/4 text-sm'
        options={presetOptions}
        onChange={(option) => onSelectPreset(option.value)}
        value={selectedPreset}
        placeholder='Select pipeline view'
      />
      {showDropdown && (
        <div
          className={overlay}
          onClick={() => setShowDropdown(false)}
        />
      )}
      <div className='relative'>
        <button
          className='bg-blue-900 text-white rounded px-1 py-1'
          data-test='three-dots-button'
          onClick={() => setShowDropdown(true)}
        >
          <EllipsisVerticalIcon width={22} />
        </button>
        {showDropdown && (
          <div
            className={`absolute w-52 -right-3 mt-5 border z-20 bg-white p-2.5 rounded-lg drop-shadow-md text-left	${after}`}
          >
            <ul>
              <li>
                <button
                  disabled={!currentPreset?.uuid || !allowDeleteOrUpdatePreset}
                  data-test='save-view-button'
                  style={{ color: KeyColors.grey.DEFAULT }}
                  onClick={handleUpdatePreset}
                  className={!allowDeleteOrUpdatePreset ? 'tooltip' : ''}
                  data-tip='You can only save views you created'
                >
                  Save View
                </button>
              </li>
              <li>
                <button
                  disabled={!currentPreset?.uuid || !allowDeleteOrUpdatePreset}
                  data-test='rename-view-button'
                  style={{ color: KeyColors.grey.DEFAULT }}
                  onClick={() => dispatch(setActiveModal(MODALS.RenamePreset))}
                  className={!allowDeleteOrUpdatePreset ? 'tooltip' : ''}
                  data-tip='You can only rename views you created'
                >
                  Rename View
                </button>
              </li>
              <li>
                <button
                  style={{ color: KeyColors.grey.DEFAULT }}
                  data-test='save-as-view-button'
                  onClick={() => dispatch(setActiveModal(MODALS.SaveAsPreset))}
                >
                  Save as
                </button>
              </li>
              <li>
                <button
                  disabled={!currentPreset?.uuid}
                  data-test='set-as-default-view-button'
                  style={{ color: KeyColors.grey.DEFAULT }}
                  onClick={() => handleSetDefaultPreset(currentPreset.uuid)}
                >
                  Set as Default
                </button>
              </li>
              <li>
                <button
                  disabled={!allowDeleteOrUpdatePreset || !currentPreset?.uuid}
                  data-test='delete-view-button'
                  style={{ color: KeyColors.red.DEFAULT }}
                  onClick={() => handleDeletePreset(currentPreset.uuid)}
                  className={!allowDeleteOrUpdatePreset ? 'tooltip' : ''}
                  data-tip='You can only delete views you created'
                >
                  Delete View
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
      <Modal
        visible={activeModal == MODALS.RenamePreset}
        toggle={toggle}
        containerSelector='#rename-preset-modal'
        title='Rename View'
        modalCSS={{ width: 400 }}
      >
        <div className='flex flex-col'>
          <KeyInput
            type='text'
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
          />
          <button onClick={() => handleRenamePreset(presetName)}>Save</button>
        </div>
      </Modal>
      <Modal
        visible={activeModal == MODALS.SaveAsPreset}
        toggle={toggle}
        containerSelector='#save-preset-modal'
        title='Save as'
        modalCSS={{ width: 400 }}
      >
        <div className='flex flex-col space-y-3'>
          {user?.is_superuser && (
            <div className='flex flex-row items-center space-x-2'>
              <KeyCheckbox
                defaultChecked={false}
                onChange={(e) => setCompanyView(e.target.checked)}
              />
              <KeyLabel>Company Preset</KeyLabel>
            </div>
          )}
          <KeyInput
            type='text'
            value={presetName}
            onChange={(e) => {
              setPresetName(e.target.value)
            }}
          />
          <button onClick={() => handleSaveAs(presetName)}>Save</button>
        </div>
      </Modal>
    </>
  )
}
